import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';

import Analytics from '@web-solutions/module-analytics';
import { setEmail as setProfileEmail } from '@web-solutions/base-app/store/profile/actions';
import { initOrders } from '@web-solutions/core/store/billing/actions';
import { useRemoteConfig } from 'core/hooks/use-remote-config';

import { useStore } from '../store/store';
import { getEnvUrl } from '../helpers/getEnvUrl';
import { applyFoodzeeAuthTokens } from '../helpers/applyFoodzeeAuthTokens';
import { buildAnonEmail } from '../helpers/anonEmail';

const STRIPE_PAYMENT_SYSTEM = 'stripe';
const TRUEGATE_PAYMENT_SYSTEM = 'truegate';

/**
 * Hairstyle funnels (hairstyle-quiz / hairstyle-women-quiz): prepare checkout.
 *
 * Unlike the weight-loss funnel, hairstyle never asks for an email — the user
 * pays first and the paywall attaches whatever the payment returned through
 * `/user/update-by-token`. So the anonymous user has to exist by the time the
 * paywall renders, ALWAYS, not only under the `collectEmailAfterPayment` A/B
 * (which is what `useAnonymousUser` gates on).
 *
 * Same two-payment-system split as `useAnonymousUser`:
 *  - Stripe:   the user's token also authorizes billing → sync it and re-init.
 *  - Truegate: billing is authorized separately by the `auth()` thunk, so the
 *              Voery token must NOT be pushed into billing. Truegate keys its
 *              customer by email, so seed the synthetic `anon-<uid>@` address
 *              and replay `initOrders` once it is in the store.
 */
export function useHairstyleCheckout() {
  const dispatch = useDispatch();
  // @ts-ignore — paymentSystem lives on the core RemoteConfig type
  const { paymentSystem } = useRemoteConfig();

  const userId = useStore((state: any) => state.userId);
  const authTokens = useStore((state: any) => state.authTokens);
  const setUserId = useStore((state: any) => state.setUserId);

  const requestedRef = useRef(false);
  const { apiBaseUrl } = getEnvUrl();

  useEffect(() => {
    if (requestedRef.current) return;

    const isStripe = paymentSystem === STRIPE_PAYMENT_SYSTEM;
    const isTruegate = paymentSystem === TRUEGATE_PAYMENT_SYSTEM;

    // Wait for the remote config to name a payment system — billing wiring
    // differs per system, and guessing would authorize the wrong one.
    if (!isStripe && !isTruegate) return;

    const seedAnonEmail = (uid?: string) => {
      if (isTruegate && uid) {
        dispatch(setProfileEmail(buildAnonEmail(uid)));
      }
    };

    // Returning visitor: re-apply the persisted tokens so billing is initialized,
    // but don't create a second anonymous user.
    if (userId || authTokens?.accessToken) {
      requestedRef.current = true;
      seedAnonEmail(userId || authTokens?.userId);
      if (authTokens?.accessToken) {
        applyFoodzeeAuthTokens(
          {
            accessToken: authTokens.accessToken,
            refreshToken: authTokens.refreshToken,
            uid: authTokens.userId || userId,
          },
          dispatch,
          { reinitBilling: true, paymentSystem, syncBillingAuth: isStripe }
        );
      }
      return;
    }

    requestedRef.current = true;

    (async () => {
      try {
        const response = await axios.post(`${apiBaseUrl}/v2/user`);

        if (response.status === 201) {
          setUserId(response.data.uid);
          seedAnonEmail(response.data.uid);
          applyFoodzeeAuthTokens(response.data, dispatch, {
            reinitBilling: true,
            paymentSystem,
            syncBillingAuth: isStripe,
          });
          if (isTruegate) {
            // @ts-ignore — the paywall's own init() already ran against an empty
            // profile email; replay it now that the synthetic address is stored.
            dispatch(initOrders());
          }
          Analytics.trackEvent('checkout', 'userCreated');
        }
      } catch (error) {
        // Allow a retry on the next render if the request failed.
        requestedRef.current = false;
        Analytics.trackEvent('checkout', 'userError');
      }
    })();
  }, [apiBaseUrl, authTokens, dispatch, paymentSystem, setUserId, userId]);
}
